// R58-L-14 — Tiles widget header: scope toggle + kind multi-select.
//
// Layout:
//   [Current image] [All images] | [AI findings ☑] [Defects ☑]
//
// All four buttons carry semantic `aria-pressed` so the acceptance spec
// (Playwright) can assert state without DOM inspection.  The scope
// toggle is mutually-exclusive; the kind multi-select is independent
// (with the guardrail that at least one kind stays checked, enforced
// in the store).
//
// SB6-ANA-TILE-03 — match the Level*Toggle chip style used by the
// FilterWidget so the Tiles header reads as the same affordance family.
// Switched from MUI <Button variant="contained|outlined"> to
// <ToggleButton> with the brand-blue tonal selected-state used by
// Level1/2/3/4 (see filter-tree/Level1Toggle.tsx).
import { Box, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";
// SB6-ANA-UI-01 — shared compact toggle preset. ANA-R2-07 switches the Tiles
// header from `tileToggleSx` (selected label = primary.main #0071E3, which axe
// measures at 3.98:1 on the ~12% tonal-blue selected background — below AA) to
// `compactToggleSx`, the AA-safe sibling whose light-mode selected label uses
// the darkened Signal Blue #0058B8. Same density; fixes the long-standing Tiles
// toggle contrast finding while we are in this file for the toolbar rework.
import { compactToggleSx } from "@aeriosiq/ui";
import { useTilesScope } from "./useTilesScope.js";

// ANA-R2-07 AC3 — one plain sentence per control, reusing the ANA-R1-12 burger
// tooltip pattern (a single sentence describing exactly what the control does).
const TOOLTIP_COPY = {
  scopeCurrent:
    "Show only the findings and defects on the image you are currently viewing.",
  scopeAll:
    "Show the findings and defects across every image in this data set that matches the active filter.",
  kindAi: "Show or hide the AI-detected findings among the tiles.",
  kindDefect: "Show or hide the confirmed defects among the tiles.",
} as const;

export interface TilesHeaderControlsProps {
  /**
   * ANA-R2-06 — finding/defect count for the "Current image" scope, rendered
   * in brackets on the toggle. Optional so the control stays reusable in
   * shells that don't compute counts (the bracket is simply omitted).
   */
  currentCount?: number;
  /** ANA-R2-06 — finding/defect count for the "All images" scope. */
  allCount?: number;
}

// ANA-R2-07 — the resting toolbar is grouped FILTERS ONLY (OQ-A2-3 c):
// scope (current/all + counts) on the left, content kind (AI findings /
// defects) flush right. The Promote-selected action no longer lives here —
// it moved to the contextual <TilesActionBar /> shown only on selection.
export function TilesHeaderControls({
  currentCount,
  allCount,
}: TilesHeaderControlsProps = {}): React.ReactElement {
  const scope = useTilesScope((s) => s.scope);
  const setScope = useTilesScope((s) => s.setScope);
  const kinds = useTilesScope((s) => s.kinds);
  const toggleKind = useTilesScope((s) => s.toggleKind);

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: 1,
        px: 1,
        py: 0.5,
        borderBottom: 1,
        borderColor: "divider",
        flexShrink: 0,
      }}
    >
      {/* ANA-R2-07 — each scope button carries its OWN `selected` + `onChange`
          (rather than relying on ToggleButtonGroup cloning its direct
          children) so the Tooltip wrappers below don't break the group's
          value injection. The group is kept for the connected-border layout
          + the `aria-label` grouping only. Tiles scope is strictly bi-state,
          so clicking the active button is a no-op (never deselects both). */}
      <ToggleButtonGroup size="small" exclusive aria-label="Tiles scope">
        <Tooltip title={TOOLTIP_COPY.scopeCurrent}>
          <ToggleButton
            value="current"
            selected={scope === "current"}
            onClick={() => {
              setScope("current");
            }}
            aria-pressed={scope === "current"}
            // ANA-R2-06/07 — a STABLE accessible name that excludes the count
            // badge (the visible "(n)" still renders) and is immune to the
            // Tooltip wrapper, so role-by-name lookups stay reliable.
            aria-label="Current image"
            sx={compactToggleSx}
            data-testid="tiles-scope-current"
          >
            Current image
            {currentCount !== undefined ? (
              <Box
                component="span"
                data-testid="tiles-scope-current-count"
                sx={{ ml: 0.5, fontVariantNumeric: "tabular-nums" }}
              >
                ({currentCount})
              </Box>
            ) : null}
          </ToggleButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_COPY.scopeAll}>
          <ToggleButton
            value="all"
            selected={scope === "all"}
            onClick={() => {
              setScope("all");
            }}
            aria-pressed={scope === "all"}
            aria-label="All images"
            sx={compactToggleSx}
            data-testid="tiles-scope-all"
          >
            All images
            {allCount !== undefined ? (
              <Box
                component="span"
                data-testid="tiles-scope-all-count"
                sx={{ ml: 0.5, fontVariantNumeric: "tabular-nums" }}
              >
                ({allCount})
              </Box>
            ) : null}
          </ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
      <Box sx={{ display: "flex", gap: 0.5, ml: "auto", alignItems: "center" }}>
        <Tooltip title={TOOLTIP_COPY.kindAi}>
          <ToggleButton
            size="small"
            value="ai"
            selected={kinds.has("ai")}
            onChange={() => {
              toggleKind("ai");
            }}
            aria-pressed={kinds.has("ai")}
            aria-label="Toggle AI findings"
            sx={compactToggleSx}
          >
            AI findings
          </ToggleButton>
        </Tooltip>
        <Tooltip title={TOOLTIP_COPY.kindDefect}>
          <ToggleButton
            size="small"
            value="defect"
            selected={kinds.has("defect")}
            onChange={() => {
              toggleKind("defect");
            }}
            aria-pressed={kinds.has("defect")}
            aria-label="Toggle defects"
            sx={compactToggleSx}
          >
            Defects
          </ToggleButton>
        </Tooltip>
      </Box>
    </Box>
  );
}
